<template>
  <v-container fluid class="mt-16 px-6">
    <v-card flat>
      <v-card-title class="d-flex align-center">
        <v-icon left color="primary">mdi-cog</v-icon>
        <h2 class="mb-0">Admin Settings</h2>
        <v-spacer></v-spacer>
      </v-card-title>
      <v-card-subtitle class="pt-1">
        One place to manage every admin setting — SMS, sales targets, user
        accounts, roles &amp; permissions, and more.
      </v-card-subtitle>

      <v-card-text>
        <div v-for="group in groups" :key="group.title" class="mb-6">
          <div class="text-overline grey--text text--darken-1 mb-2">
            <v-icon small class="mr-1">{{ group.icon }}</v-icon>
            {{ group.title }}
          </div>
          <v-row>
            <v-col
              v-for="setting in group.settings"
              :key="setting.name"
              cols="12"
              sm="6"
              md="4"
              lg="3"
            >
              <v-card
                outlined
                hover
                class="fill-height d-flex flex-column"
                @click="openSetting(setting)"
              >
                <v-card-text class="d-flex align-start">
                  <v-avatar :color="setting.color" size="44" class="mr-3">
                    <v-icon dark>{{ setting.icon }}</v-icon>
                  </v-avatar>
                  <div>
                    <div class="subtitle-1 font-weight-medium">
                      {{ setting.name }}
                    </div>
                    <div class="caption grey--text text--darken-1">
                      {{ setting.description }}
                    </div>
                  </div>
                </v-card-text>
                <v-spacer></v-spacer>
                <v-card-actions class="pt-0">
                  <v-spacer></v-spacer>
                  <v-icon small color="grey">
                    {{
                      setting.action === "modal" ? "mdi-cog" : "mdi-arrow-right"
                    }}
                  </v-icon>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </v-card-text>

      <p class="caption grey--text text-right pr-4 pb-2 mb-0">
        AdminSettings.vue
      </p>
    </v-card>

    <!-- SMS Notification Settings Dialog (opened inline from this hub) -->
    <sms-settings-modal :show.sync="smsSettingsDialog" />

  </v-container>
</template>

<script>
import { mapActions } from "vuex";
import SmsSettingsModal from "@/components/Sms/smsSettingsModal.vue";

export default {
  name: "AdminSettings",
  components: { SmsSettingsModal },
  data() {
    return {
      smsSettingsDialog: false,
      groups: [
        {
          title: "Communications",
          icon: "mdi-message-text-outline",
          settings: [
            {
              name: "SMS Settings",
              description:
                "Sender ID, enable/disable, and per-event message templates.",
              icon: "mdi-message-cog",
              color: "teal",
              action: "modal",
              handler: "openSmsSettings",
            },
          ],
        },
        {
          title: "Sales",
          icon: "mdi-chart-box-outline",
          settings: [
            {
              name: "Sales Targets",
              description:
                "Set monthly minimum &amp; target amounts per sales agent.",
              icon: "mdi-bullseye-arrow",
              color: "deep-orange",
              action: "route",
              route: "/sales",
            },
          ],
        },
        {
          title: "Users & Access",
          icon: "mdi-account-key-outline",
          settings: [
            {
              name: "User Accounts",
              description: "Create, edit, and deactivate staff login accounts.",
              icon: "mdi-account-cog",
              color: "indigo",
              action: "route",
              route: "/user_accounts",
            },
            {
              name: "Roles & Permissions",
              description: "Fine-grained feature gates and role restrictions.",
              icon: "mdi-shield-lock-outline",
              color: "red darken-1",
              action: "route",
              route: "/role_restrictions",
            },
            {
              name: "Printable Forms",
              description: "Assign printable forms to hats.",
              icon: "mdi-printer",
              color: "blue-grey",
              action: "route",
              route: "/printable-forms",
            },
          ],
        },
        {
          title: "Reports",
          icon: "mdi-chart-box-outline",
          settings: [
            {
              name: "Inventory Reports",
              description: "Stock movement, valuation, and inventory reports.",
              icon: "mdi-chart-bar",
              color: "light-blue darken-2",
              action: "route",
              route: "/inventory_reports",
            },
            {
              name: "Service Reports",
              description: "Repair and service performance reports.",
              icon: "mdi-chart-box",
              color: "teal darken-2",
              action: "route",
              route: "/service_reports",
            },
          ],
        },
        {
          title: "Organization",
          icon: "mdi-domain",
          settings: [
            {
              name: "Branches",
              description: "Manage shop branches and their details.",
              icon: "mdi-office-building",
              color: "blue-grey",
              action: "route",
              route: "/branches",
            },
            {
              name: "Warranty Policies",
              description: "Define warranty policies applied to units & items.",
              icon: "mdi-shield-check",
              color: "green darken-1",
              action: "route",
              route: "/warranty_policies",
            },
            {
              name: "Task Management",
              description: "Assign and track internal staff tasks.",
              icon: "mdi-clipboard-check-outline",
              color: "cyan darken-1",
              action: "route",
              route: "/task_management",
            },
          ],
        },
        {
          title: "Data & System",
          icon: "mdi-database-cog-outline",
          settings: [
            {
              name: "Batch Imports",
              description: "Bulk-import inventory and other records.",
              icon: "mdi-database-import",
              color: "brown",
              action: "route",
              route: "/batch_import",
            },
            {
              name: "Suppliers",
              description: "Manage supplier records and contacts.",
              icon: "mdi-store",
              color: "amber darken-2",
              action: "route",
              route: "/suppliers",
            },
          ],
        },
      ],
    };
  },
  methods: {
    ...mapActions({
      get_sms_settings: "sms/get_sms_settings",
    }),
    openSetting(setting) {
      if (setting.action === "modal" && setting.handler) {
        this[setting.handler]();
      } else if (setting.action === "route" && setting.route) {
        if (this.$route.path !== setting.route)
          this.$router.push(setting.route);
      }
    },
    openSmsSettings() {
      this.get_sms_settings();
      this.smsSettingsDialog = true;
    },
  },
};
</script>

<style lang="scss" scoped></style>
